// src/utils/notificationReadState.js
//
// Which bell notifications the user has already dealt with.
//
// The bell's items are derived at read time from live endpoints (see
// useNotifications.js), so there is no server-side notification row to flag as
// read. Read state therefore lives in the browser, per account, and is applied
// on top of each poll:
//
//   readAt   id -> epoch ms the user cleared it. An item stays hidden while
//            nothing newer has happened to it (see isRead) — a NEW message in
//            the same thread, later than readAt, brings it back.
//   allReadUpTo  kind -> epoch ms, set by "Mark all read": the newest
//            at/revisedAt among that kind's items when it was clicked. An item
//            no later than its kind's watermark counts as read, however many
//            there were — a per-item marker for each, capped at
//            MAX_READ_MARKERS, used to let anything past the 300th come back
//            on the next poll. Per kind, and taken from the items' own times
//            rather than this browser's clock, because each source has its own
//            clock: chat times arrive as zone-less server LocalDateTimes that an
//            IST browser reads ~5.5h early, so a watermark of Date.now() would
//            hide the next several hours of new messages.
//   history  snapshots of the last MAX_READ_HISTORY cleared items, newest
//            first, so the "Read" tab can still show them after the source
//            stops reporting them (e.g. a thread whose unread count the server
//            already reset when it was opened). A snapshot keeps the item's
//            kind and ref, not a route — see utils/notificationTarget.js.
//
// Storage failures (private window, blocked site data) degrade to in-memory
// behaviour for the session rather than throwing.

export const MAX_READ_HISTORY = 20;

// readAt has to outlive the items it hides (a care-plan action stays on the
// bell until completed), but must not grow without bound. Only single-item
// clears rely on it — "Mark all read" is covered by allReadUpTo.
const MAX_READ_MARKERS = 300;

export const NOTIFICATIONS_READ_EVENT = 'previu:notifications-read';

export const EMPTY_READ_STATE = { readAt: {}, allReadUpTo: {}, history: [] };

function tokenSubject() {
  try {
    const jwt = localStorage.getItem('previu_token');
    const payload = jwt && JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.sub || '';
  } catch {
    return '';
  }
}

/** One store per signed-in account and portal, so two people sharing a browser don't clear each other's bell. */
export function readStateKey(role, patientId) {
  return `previu_notif_read_v1:${role}:${patientId || tokenSubject()}`;
}

const isTime = v => typeof v === 'number' && Number.isFinite(v) && v > 0;
const isMap = v => !!v && typeof v === 'object' && !Array.isArray(v);
const timesOnly = map => (isMap(map)
  ? Object.fromEntries(Object.entries(map).filter(([, t]) => isTime(t)))
  : {});

/**
 * Tolerates anything an older build (or a hand-edited store) left behind:
 * state saved before allReadUpTo existed loads with none, and malformed
 * markers, watermarks or snapshots are dropped rather than trusted. A single
 * numeric allReadUpTo (an earlier draft of this change) is dropped too: it
 * doesn't say which kinds it covered, and applying it to all of them is the
 * clock-offset problem the per-kind watermark exists to avoid. What it hid
 * shows as unread once more.
 */
export function loadReadState(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    if (!isMap(parsed)) return EMPTY_READ_STATE;
    return {
      readAt: timesOnly(parsed.readAt),
      allReadUpTo: timesOnly(parsed.allReadUpTo),
      history: Array.isArray(parsed.history)
        ? parsed.history.filter(h => h && typeof h === 'object' && typeof h.id === 'string').slice(0, MAX_READ_HISTORY)
        : [],
    };
  } catch {
    return EMPTY_READ_STATE;
  }
}

export function saveReadState(key, state) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Not persisted — still correct for this session.
  }
}

/** Epoch ms of the item's last change, or NaN when it has no usable time. */
function changedAt(item) {
  const changed = item.revisedAt ?? item.at;
  return changed ? new Date(changed).getTime() : NaN;
}

/**
 * An item counts as read when it was cleared AND nothing has changed since —
 * cleared either on its own (a marker) or by "Mark all read" (allReadUpTo).
 * `revisedAt` lets a source name the moment its content last changed when `at`
 * is not stable (lab-day reminders are stamped "now" on every poll).
 */
export function isRead(item, state) {
  const t = changedAt(item);
  const clearedAt = state.readAt[item.id];
  if (clearedAt && (Number.isNaN(t) || t <= clearedAt)) return true;
  const watermark = item.kind && state.allReadUpTo[item.kind];
  return !Number.isNaN(t) && !!watermark && t <= watermark;
}

function snapshotsOf(state, items, now) {
  const ids = new Set(items.map(i => i.id));
  const snapshots = items.map(i => ({
    id: i.id, severity: i.severity, icon: i.icon, title: i.title, body: i.body,
    at: i.at, kind: i.kind, ref: i.ref, readAt: now,
  }));
  return [...snapshots, ...state.history.filter(h => !ids.has(h.id))].slice(0, MAX_READ_HISTORY);
}

function withMarkers(readAt, items, now) {
  const next = { ...readAt };
  items.forEach(item => { next[item.id] = now; });
  return Object.fromEntries(Object.entries(next)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_READ_MARKERS));
}

/** Returns the next state with `items` marked read at `now`. */
export function withMarkedRead(state, items, now = Date.now()) {
  if (items.length === 0) return state;
  return {
    readAt: withMarkers(state.readAt, items, now),
    allReadUpTo: state.allReadUpTo,
    history: snapshotsOf(state, items, now),
  };
}

/**
 * Returns the next state with `items` — what the bell is showing as unread —
 * all read. Each kind's watermark moves up to the newest of its items (never
 * down). Items with no usable time, or no kind, can't be compared against one
 * and get a marker instead (the standing upgrade prompt, at most a handful).
 */
export function withAllMarkedRead(state, items, now = Date.now()) {
  if (items.length === 0) return state;
  const allReadUpTo = { ...state.allReadUpTo };
  const unstamped = [];
  items.forEach(item => {
    const t = changedAt(item);
    if (!item.kind || Number.isNaN(t)) unstamped.push(item);
    else allReadUpTo[item.kind] = Math.max(allReadUpTo[item.kind] || 0, t);
  });
  return {
    readAt: withMarkers(state.readAt, unstamped, now),
    allReadUpTo,
    history: snapshotsOf(state, items, now),
  };
}

/**
 * Lets any screen clear bell notifications by id without owning the bell's
 * state — the care team page uses it when a conversation is opened. The
 * mounted bell listens for the event and resolves the ids against its items.
 */
export function requestMarkNotificationsRead(ids) {
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_READ_EVENT, { detail: { ids } }));
}
